import { useEffect } from 'react'

import { get } from 'lodash'
import { useFormContext, useWatch, Path } from 'react-hook-form'

import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { useOrgCompliance } from '~/hooks/compliance'
import { getChainLabel } from '~/utils/chainLabels'

/**
 * Handles validation of compliant payment relations for all transfer flows (forms of type TransferFormValues).
 * Calls setError and clearErrors to update the form state with any validation errors.
 */
export const useCompliantPaymentValidation = () => {
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<TransferFormValues>()
  const recipients = useWatch({ control, name: 'recipients' })
  const transferToken = useWatch({ control, name: 'transferToken' })

  const { payees } = useOrgCompliance()

  useEffect(
    function runValidation() {
      if (
        !recipients ||
        recipients.length === 0 ||
        !transferToken?.chainId ||
        !transferToken?.token
      ) {
        // Clear only the errors this hook owns — a bare clearErrors() wiped
        // every other validator's errors on token/recipient blips
        recipients?.forEach((_, index) => {
          const fieldPath =
            `recipients.${index}.receiver.compliantPaymentRelationId` as Path<TransferFormValues>
          if (get(errors, fieldPath)) {
            clearErrors(fieldPath)
          }
        })
        return
      }

      recipients.forEach((recipient, index) => {
        const fieldPath =
          `recipients.${index}.receiver.compliantPaymentRelationId` as Path<TransferFormValues>
        const existingError = get(errors, fieldPath)

        const compliantPaymentRelationId =
          recipient.receiver?.compliantPaymentRelationId

        // Only validate if there's a compliant payment relation ID
        if (!compliantPaymentRelationId) {
          if (existingError) {
            clearErrors(fieldPath)
          }
          return
        }

        const payee = payees.find((p) => p.id === compliantPaymentRelationId)

        // Validate chain support
        const isInvalidChain =
          !payee || !payee.chains?.includes(transferToken.chainId)

        // Determine which error to show (prioritize chain error over token error)
        let errorMessage: string | null = null
        if (isInvalidChain) {
          if (!payee) {
            errorMessage = 'Compliant recipient not found'
          } else {
            errorMessage = `Compliant recipient ${payee.payeeOrg?.name} does not accept payments on ${getChainLabel(transferToken.chainId)}`
          }
        }

        // Set or clear error based on validation results
        if (errorMessage) {
          // Only set error if it's different from existing error to avoid unnecessary re-renders
          if (existingError?.message !== errorMessage) {
            setError(fieldPath, {
              type: 'manual',
              message: errorMessage,
            })
          }
        } else if (existingError) {
          // Clear error if validation passes
          clearErrors(fieldPath)
        }
      })
    },
    [recipients, transferToken, payees, errors, setError, clearErrors],
  )
}
